import { parsePublishableKey } from '@clerk/shared/keys';
import type { LoadedClerk } from '@clerk/shared/types';
import { versionSelector } from '@clerk/shared/versionSelector';
import type { AuthClient, Outcome } from '../../../shared/auth/flow';
import {
  PROVIDER_STRATEGY,
  type AuthMode,
  type Provider,
} from '../../../shared/auth/providers';

const LOAD_TIMEOUT_MS = 15_000;

/**
 * The address of Clerk's browser script, as @clerk/react builds it for the
 * dashboard (loadClerkJsScript in @clerk/shared): the key's Frontend API,
 * and the clerk-js version the installed @clerk/shared names. The same
 * functions pick both, so the two pages run the same Clerk. The dashboard
 * sets no proxy or domain, so neither is handled here.
 */
export function clerkScriptUrl(publishableKey: string): string | null {
  const host = parsePublishableKey(publishableKey)?.frontendApi;
  if (!host) return null;
  return `https://${host}/npm/@clerk/clerk-js@${versionSelector(undefined)}/dist/clerk.browser.js`;
}

/** What the script puts on window: Clerk, still to be loaded. */
type ScriptClerk = LoadedClerk & { load(): Promise<void> };

const current = () => (window as unknown as { Clerk?: ScriptClerk }).Clerk;

/** Clerk's script on this page, once: it makes window.Clerk from the key. */
function loadScript(publishableKey: string): Promise<ScriptClerk> {
  const loaded = current();
  if (loaded) return Promise.resolve(loaded);
  const src = clerkScriptUrl(publishableKey);
  if (!src) return Promise.reject(new Error('Not a Clerk publishable key.'));
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.setAttribute('data-clerk-publishable-key', publishableKey);
    script.setAttribute('data-clerk-js-script', '');
    const timer = window.setTimeout(
      () => reject(new Error('Clerk took too long to load.')),
      LOAD_TIMEOUT_MS,
    );
    script.addEventListener('load', () => {
      window.clearTimeout(timer);
      const clerk = current();
      if (clerk) resolve(clerk);
      else reject(new Error('Clerk did not load.'));
    });
    script.addEventListener('error', () => {
      window.clearTimeout(timer);
      script.remove();
      reject(new Error('Clerk could not be loaded.'));
    });
    document.head.appendChild(script);
  });
}

/** Clerk's refusal: the first error's code, and its words for people. */
export function outcome(error: unknown): Outcome {
  if (!error) return null;
  const first = (
    error as {
      errors?: { code?: string; longMessage?: string; message?: string }[];
    }
  ).errors?.[0];
  if (first) {
    return {
      code: first.code ?? null,
      message: first.longMessage ?? first.message ?? null,
    };
  }
  return {
    code: null,
    message: error instanceof Error ? error.message : null,
  };
}

/**
 * Right code, but Clerk wants more before there is a session: a second
 * factor, or a sign-up field the instance requires. As the sign-in page
 * says it (web/src/auth/clerk.ts).
 */
function incomplete(status: string | null): Outcome {
  return { code: `incomplete_${status ?? 'unknown'}`, message: null };
}

export interface PageClerkOptions {
  /** Clerk's publishable key, from the page (sessionLocals); null without one. */
  publishableKey: string | null;
  /**
   * Where Google or Apple come back to (the sign-in page's OAuth return),
   * and where the account goes after: oauthReturn in ../onboarding.ts.
   */
  oauthReturn(provider: Provider): {
    callbackUrl: string;
    destinationUrl: string;
  };
  /** The browser holds a session already: on to the dashboard. */
  signedIn(): void;
}

/**
 * The sign-in page's AuthClient (web/src/auth/clerk.ts) for a page without
 * React, on Clerk's browser script: the same steps, so /get-started's step 4
 * signs up, or in, by an emailed code or by Google or Apple without leaving
 * the page. The script is loaded on the first step taken, as most visitors
 * never take one and it is large, by the same address the dashboard loads it
 * from. The sign-in page uses Clerk's newer resource API through
 * @clerk/react; this uses the resources Clerk's script itself documents.
 * "By continuing you agree to the Terms and Privacy policy" is on the page,
 * as there, which `legalAccepted` records.
 */
export function pageClerk(options: PageClerkOptions): AuthClient {
  let ready: Promise<LoadedClerk> | null = null;
  const clerk = (): Promise<LoadedClerk> => {
    const { publishableKey } = options;
    if (!publishableKey) {
      return Promise.reject(new Error('Sign-in is not set up here.'));
    }
    ready ??= loadScript(publishableKey).then(async (c) => {
      await c.load();
      return c;
    });
    // A failed load is tried again on the next step, not remembered.
    ready.catch(() => {
      ready = null;
    });
    return ready;
  };

  /** Each step: Clerk's refusal as an Outcome, null when it went through. */
  const step = async (run: (c: LoadedClerk) => Promise<Outcome>) => {
    try {
      return await run(await clerk());
    } catch (error) {
      return outcome(error) ?? { code: null, message: null };
    }
  };

  /** Emails the sign-in's code to the address it was started for. */
  const sendSignInCode = async (c: LoadedClerk): Promise<Outcome> => {
    const signIn = c.client.signIn;
    const factor = signIn.supportedFirstFactors?.find(
      (f) => f.strategy === 'email_code',
    );
    if (!factor || !('emailAddressId' in factor)) {
      return { code: 'no_email_code', message: null };
    }
    await signIn.prepareFirstFactor({
      strategy: 'email_code',
      emailAddressId: factor.emailAddressId,
    });
    return null;
  };

  const activate = async (
    c: LoadedClerk,
    status: string | null,
    session: string | null,
  ): Promise<Outcome> => {
    if (status !== 'complete' || !session) return incomplete(status);
    await c.setActive({ session });
    return null;
  };

  return {
    sendCode: (mode: AuthMode, email: string) =>
      step(async (c) => {
        if (mode === 'signin') {
          await c.client.signIn.create({ identifier: email });
          return sendSignInCode(c);
        }
        await c.client.signUp.create({
          emailAddress: email,
          legalAccepted: true,
        });
        await c.client.signUp.prepareEmailAddressVerification({
          strategy: 'email_code',
        });
        return null;
      }),

    resendCode: (mode: AuthMode) =>
      step(async (c) => {
        if (mode === 'signin') return sendSignInCode(c);
        await c.client.signUp.prepareEmailAddressVerification({
          strategy: 'email_code',
        });
        return null;
      }),

    verifyCode: (mode: AuthMode, code: string) =>
      step(async (c) => {
        if (mode === 'signin') {
          const signIn = await c.client.signIn.attemptFirstFactor({
            strategy: 'email_code',
            code,
          });
          return activate(c, signIn.status, signIn.createdSessionId);
        }
        const signUp = await c.client.signUp.attemptEmailAddressVerification({
          code,
        });
        return activate(c, signUp.status, signUp.createdSessionId);
      }),

    sso: (mode: AuthMode, provider: Provider) =>
      step(async (c) => {
        if (c.user) {
          options.signedIn();
          return null;
        }
        const { callbackUrl, destinationUrl } = options.oauthReturn(provider);
        const params = {
          strategy: PROVIDER_STRATEGY[provider],
          redirectUrl: callbackUrl,
          redirectUrlComplete: destinationUrl,
        };
        if (mode === 'signin') {
          await c.client.signIn.authenticateWithRedirect(params);
        } else {
          await c.client.signUp.authenticateWithRedirect({
            ...params,
            legalAccepted: true,
          });
        }
        return null;
      }),
  };
}
